import React, { useState } from 'react';
import Dialog from 'components/common/react-dialog';
import Wahanda from 'common/wahanda';
import { Button } from 'components/common/Button';
import { Email, Warning } from 'components/common/Icon';
import { BOOKING_ACTOR } from 'common/consts';
import { NoShowFlowAnalytics } from 'common/analytics';
import App from 'common/backbone-app';
import { NoShowOverPhone } from './NoShowOverPhone/NoShowOverPhone';
import style from './NoShowDialog.scss';
import { AppointmentInfoBoxes } from '../AppointmentInfoBoxes/AppointmentInfoBoxes';
import { PaymentProtection } from '../PaymentProtection';
import { PaymentProtectionBanner } from '../PaymentProtection/PaymentProtectionBanner';
import { usePaymentProtectionQuery } from '../PaymentProtection/usePaymentProtectionQuery';

interface Props {
  type?: 'over-phone';
  onClose: () => void;
  onDoNoShow: (_?: { preventPaymentProtection: boolean | null }) => void;
  isPrepaid: boolean;
  bookingActor: BOOKING_ACTOR;
  consumerId: number;
  isWalkin: boolean;
  paymentProtected: boolean;
  treatwellFeePercentage: number;
  granularity: 'appointment' | 'appointment-group';
  id: number;
}

export const NoShowDialog = (props: Props) => {
  const {
    type,
    onClose,
    onDoNoShow,
    consumerId,
    isWalkin,
    isPrepaid,
    bookingActor,
    paymentProtected,
    granularity,
    id,
    treatwellFeePercentage,
  } = props;

  const [preventPaymentProtection, setPreventPaymentProtection] = useState(false);
  const [isDirty, setDirty] = useState(false);
  const { isLoading, response } = usePaymentProtectionQuery(
    granularity,
    id,
    paymentProtected,
    'no-show',
  );

  const isPaymentProtectionVisible =
    paymentProtected && !isLoading && Boolean(response?.canPreventForNoShow);

  if (type === 'over-phone') {
    return <NoShowOverPhone onClose={onClose} />;
  }

  const onPaymentProtectionChange = (value: boolean) => {
    setPreventPaymentProtection(value);
    setDirty(true);
    NoShowFlowAnalytics.trackPaymentProtection({ preventPaymentProtection: value });
  };

  const isSubmitDisabled = () => {
    if (!paymentProtected) {
      return false;
    }

    return isLoading || (isPaymentProtectionVisible && !isDirty);
  };

  const onNoShowClick = () => {
    onDoNoShow({
      preventPaymentProtection: isPaymentProtectionVisible ? preventPaymentProtection : null,
    });
    onClose();

    if (isPaymentProtectionVisible) {
      NoShowFlowAnalytics.trackPaymentProtectionSubmit({ preventPaymentProtection });
    }

    NoShowFlowAnalytics.trackNoShowConfirmation();
    if (!isWalkin && App.config.canShowPrepayRequirementFeatures()) {
      App.ES6.Initializers.PrepayRequireDialog({
        customerId: consumerId,
      }).render();
    }
  };

  const isComissionAbuser = App.config.get('venue').commissionAbuser;

  return (
    <Dialog
      dataTest="no-show-modal"
      width={420}
      title={Wahanda.lang.calendar.appointments.noShow.requestNoShowPhone.title}
      onClose={onClose}
      classes={style.dialog}
    >
      <AppointmentInfoBoxes isPrepaid={isPrepaid} bookingActor={bookingActor} />
      {isPaymentProtectionVisible && <PaymentProtectionBanner />}
      {isPaymentProtectionVisible && (
        <PaymentProtection flow="no-show" onChange={onPaymentProtectionChange} />
      )}
      {isComissionAbuser && treatwellFeePercentage > 0 ? (
        <div className={style.warning} data-test="comission-abuser-warning">
          <h3 className={style.title}>{Wahanda.lang.calendar.appointments.noShow.warningTitle}</h3>
          <div className={style.banner}>
            <Warning size="small" />
            <p>{Wahanda.lang.calendar.appointments.noShow.warningText}</p>
          </div>
        </div>
      ) : (
        <h3 className={style.title}>
          {Wahanda.lang.calendar.appointments.noShow.notificationTitle}
        </h3>
      )}
      <div className={style.notification}>
        <Email size="small" />
        <p>{Wahanda.lang.calendar.appointments.noShow.notificationText}</p>
      </div>
      <div className={style.buttons}>
        <Button
          label={Wahanda.lang.calendar.appointments.noShow.buttons.defaultAction}
          fullWidth
          colour="alert"
          size="large"
          onClick={onNoShowClick}
          disabled={isSubmitDisabled()}
          dataTest="no-show-submit"
        />
        <Button
          label={Wahanda.lang.shared.buttons.cancel}
          fullWidth
          variant="hollow"
          size="large"
          onClick={onClose}
        />
      </div>
    </Dialog>
  );
};
